import { byCode, computeCrawlerTrust } from "@shopexperts/shared";
import type {
  CrawlerProfileExtras,
  ShopexpertsCrawlerProfilePayload,
  ShopexpertsPreviewExpert,
  ShopifyReviewItem,
} from "@shopexperts/shared";

import type { PartnerDirectoryRecord } from "@/app/services/partnerDirectoryApi.service";

/**
 * Builds the {@link ShopexpertsCrawlerProfilePayload} sent over the
 * `shopexperts-crawler-profile` iframe bridge to the public `/partner-preview`
 * page.
 *
 * The `expert` half is produced by the scraper page (reusing the existing
 * preview-body mapping) and passed in. This module maps the crawler-only
 * {@link CrawlerProfileExtras}.
 *
 * Source priority for every field: admin-curated `mappedPayload` wins, then
 * `normalizedPayload`, then the raw scraped `rawPayload.profile`. Absent values
 * stay `undefined` — never fabricated.
 */
export function mapRecordToCrawlerProfilePayload(
  record: PartnerDirectoryRecord,
  expert: ShopexpertsPreviewExpert,
): ShopexpertsCrawlerProfilePayload {
  return {
    expert,
    crawler: mapCrawlerExtras(record, expert),
  };
}

/** Known keys we already consume from the admin-curated mapped payload. */
const KNOWN_MAPPED_KEYS = new Set([
  "expert_id",
  "expert_type",
  "agency_name",
  "first_name",
  "last_name",
  "bio",
  "country",
  "starting_price",
  "timezone",
  "phone_no",
  "languages",
  "social_links",
  "subcategory_slugs",
]);

function mapCrawlerExtras(record: PartnerDirectoryRecord, expert: ShopexpertsPreviewExpert): CrawlerProfileExtras {
  const mapped = objectValue(record.mappedPayload);
  const normalized = objectValue(record.normalizedPayload) ?? {};
  const profile = objectValue(objectValue(record.rawPayload)?.profile) ?? {};

  const extras: CrawlerProfileExtras = {
    // Crawler-sourced profiles are never claimed. No claim_url: real profiles
    // open the claim popup instead of navigating to a dashboard route.
    claim: {
      claimed: false,
    },
  };

  const source = mapSource(record, profile);
  if (source) extras.source = source;

  const meta = mapMeta(record, mapped, normalized, profile);
  if (meta) extras.meta = meta;

  const regionsServed = uniqueStrings(arrayValue(profile.supportedLocations));
  if (regionsServed.length) extras.regions_served = regionsServed;

  const links = mapLinks(record, mapped, profile);
  if (links.length) extras.links = links;

  const shopifyReviews = mapShopifyReviews(record, normalized, profile);
  if (shopifyReviews) extras.shopify_reviews = shopifyReviews;

  extras.trust = mapTrust(record, mapped, normalized, profile, expert);

  const extra = mapExtraPassthrough(mapped);
  if (extra) extras.extra = extra;

  return extras;
}

function mapSource(record: PartnerDirectoryRecord, profile: RawObject): CrawlerProfileExtras["source"] {
  const profileUrl = sanitizeHttpUrl(record.sourceUrl);
  const slug = stringOrUndefined(record.sourceSlug) ?? stringOrUndefined(profile.slug);

  const source: NonNullable<CrawlerProfileExtras["source"]> = { platform: "shopify" };
  if (profileUrl) source.profile_url = profileUrl;
  if (slug) source.slug = slug;
  return source;
}

function mapMeta(
  record: PartnerDirectoryRecord,
  mapped: RawObject | null,
  normalized: RawObject,
  profile: RawObject,
): CrawlerProfileExtras["meta"] | undefined {
  const contact = objectValue(profile.contact) ?? {};
  const socialLinks = objectValue(mapped?.social_links) ?? {};

  const meta: NonNullable<CrawlerProfileExtras["meta"]> = {};

  const location =
    stringOrUndefined(record.primaryLocation) ??
    stringOrUndefined(normalized.primaryLocation) ??
    stringOrUndefined(profile.primaryLocation);
  if (location) meta.location = location;

  const startingPriceLabel = mapStartingPriceLabel(record, mapped, normalized, profile);
  if (startingPriceLabel) meta.starting_price_label = startingPriceLabel;

  // Mapped payloads store ISO codes; scraped profiles store display names.
  // Normalize everything to English display names for the profile page.
  const languages = uniqueStrings(
    uniqueStrings(arrayValue(mapped?.languages).length ? arrayValue(mapped?.languages) : arrayValue(profile.languages))
      .map((language) => byCode[language]?.name ?? language),
  );
  if (languages.length) meta.languages = languages;

  const timezone = stringOrUndefined(mapped?.timezone);
  if (timezone) meta.timezone = timezone;

  const website = firstSanitizedHttpUrl(
    socialLinks.website,
    record.websiteUrl,
    normalized.websiteUrl,
    contact.website,
  );
  if (website) meta.website = website;

  const email = stringOrUndefined(contact.email);
  if (email) meta.email = email;

  const phone = stringOrUndefined(mapped?.phone_no) ?? stringOrUndefined(contact.phone);
  if (phone) meta.phone = phone;

  const shopifyPartnerSince = stringOrUndefined(profile.partnerSince);
  if (shopifyPartnerSince) meta.shopify_partner_since = shopifyPartnerSince;

  const partnerTier = stringOrUndefined(profile.tier);
  if (partnerTier) meta.partner_tier = partnerTier;

  return Object.keys(meta).length ? meta : undefined;
}

function mapStartingPriceLabel(
  record: PartnerDirectoryRecord,
  mapped: RawObject | null,
  normalized: RawObject,
  profile: RawObject,
): string | undefined {
  // Admin-curated dollar amount wins.
  const mappedPrice = numberOrUndefined(mapped?.starting_price);
  if (mappedPrice && mappedPrice > 0) return formatUsd(mappedPrice);

  // Real scraped price-range label (e.g. "$5,000+").
  const priceRangeLabel = stringOrUndefined(objectValue(profile.priceRange)?.label);
  if (priceRangeLabel) return priceRangeLabel;

  const cents = numberOrUndefined(record.startingPriceCents) ?? numberOrUndefined(normalized.startingPriceCents);
  if (cents && cents > 0) return formatUsd(cents / 100);

  return undefined;
}

function mapLinks(
  record: PartnerDirectoryRecord,
  mapped: RawObject | null,
  profile: RawObject,
): NonNullable<CrawlerProfileExtras["links"]> {
  const socialLinks = objectValue(mapped?.social_links) ?? {};
  const contact = objectValue(profile.contact) ?? {};
  const links: NonNullable<CrawlerProfileExtras["links"]> = [];
  const seen = new Set<string>();

  const push = (value: unknown, label: string, icon: string, sanitizer = sanitizeHttpUrl) => {
    const url = sanitizer(value);
    if (!url || seen.has(url)) return;
    seen.add(url);
    links.push({ label, url, icon });
  };

  push(socialLinks.linkedin_url, "LinkedIn", "linkedin", sanitizeLinkedInUrl);
  push(socialLinks.x_url, "X", "x");
  push(
    firstSanitizedHttpUrl(socialLinks.website, record.websiteUrl, contact.website),
    "Website",
    "website",
  );
  push(
    firstSanitizedHttpUrl(socialLinks.shopify_directory_link, record.sourceUrl),
    "Shopify Partner Directory",
    "shopify",
  );

  return links;
}

function mapShopifyReviews(
  record: PartnerDirectoryRecord,
  normalized: RawObject,
  profile: RawObject,
): CrawlerProfileExtras["shopify_reviews"] | undefined {
  const ratingSummary = objectValue(profile.ratingSummary) ?? {};
  const rawRating = objectValue(profile.rating) ?? {};

  const rating =
    numberOrUndefined(record.rating) ??
    numberOrUndefined(normalized.rating) ??
    numberOrUndefined(ratingSummary.overallRating) ??
    numberOrUndefined(rawRating.value);
  const count =
    numberOrUndefined(record.reviewCount) ??
    numberOrUndefined(normalized.reviewCount) ??
    numberOrUndefined(ratingSummary.reviewCount) ??
    numberOrUndefined(rawRating.reviewCount);

  const items = arrayValue(profile.reviews)
    .map(mapReviewItem)
    .filter((item): item is ShopifyReviewItem => item !== null);

  const histogram = arrayValue(ratingSummary.distribution)
    .map((entry) => {
      const obj = objectValue(entry);
      const stars = numberOrUndefined(obj?.stars);
      const c = numberOrUndefined(obj?.count);
      return typeof stars === "number" && typeof c === "number" ? { stars, count: c } : null;
    })
    .filter((entry): entry is { stars: number; count: number } => entry !== null);

  // Nothing meaningful to show.
  if (rating === undefined && count === undefined && items.length === 0 && histogram.length === 0) {
    return undefined;
  }

  const reviews: NonNullable<CrawlerProfileExtras["shopify_reviews"]> = { items };
  if (rating !== undefined) reviews.rating = rating;
  if (count !== undefined) reviews.count = count;
  if (histogram.length) reviews.histogram = histogram;

  const allReviewsUrl = sanitizeHttpUrl(record.sourceUrl);
  if (allReviewsUrl) reviews.all_reviews_url = allReviewsUrl;

  return reviews;
}

function mapReviewItem(raw: unknown): ShopifyReviewItem | null {
  const review = objectValue(raw);
  if (!review) return null;

  const author = stringOrUndefined(review.author);
  const text = stringOrUndefined(review.text);
  if (!author && !text) return null;

  // Shopify's per-review breakdown ("Quality of work" / "Communication").
  // Kept open-ended so future sources with different criteria flow through
  // unchanged; the profile page derives the displayed rating from these.
  const RATING_CRITERIA_LABELS: Record<string, string> = {
    qualityOfWork: "Quality of work",
    communication: "Communication",
  };
  const rawRatings = objectValue(review.ratings) ?? {};
  const criteria = Object.entries(rawRatings).flatMap(([key, value]) => {
    const criterionRating = numberOrUndefined(value);
    if (criterionRating === undefined) return [];
    return [{ label: RATING_CRITERIA_LABELS[key] ?? key, rating: clampRating(criterionRating) }];
  });

  const criteriaAverage = criteria.length
    ? criteria.reduce((total, criterion) => total + criterion.rating, 0) / criteria.length
    : undefined;

  const item: ShopifyReviewItem = {
    author: author ?? "",
    rating: clampRating(numberOrUndefined(review.rating) ?? criteriaAverage),
    text: text ?? "",
    // These records are, by definition, imported from the Shopify directory.
    source: "Imported from Shopify",
  };

  if (criteria.length) item.criteria = criteria;

  const date = stringOrUndefined(review.date);
  if (date) item.date = date;

  const serviceReviewed = stringOrUndefined(review.serviceReviewed);
  if (serviceReviewed) item.service_reviewed = serviceReviewed;

  return item;
}

/**
 * Derives the official trust/verification signals from the record and computes
 * the score breakdown through the shared model ({@link computeCrawlerTrust}),
 * so the preview always shows the full Trust /60, Verification /40,
 * Overall /100 card with missing signals and the signals breakdown.
 */
function mapTrust(
  record: PartnerDirectoryRecord,
  mapped: RawObject | null,
  normalized: RawObject,
  profile: RawObject,
  expert: ShopexpertsPreviewExpert,
): NonNullable<CrawlerProfileExtras["trust"]> {
  const contact = objectValue(profile.contact) ?? {};
  const socialLinks = objectValue(mapped?.social_links) ?? {};

  const rating =
    numberOrUndefined(record.rating) ??
    numberOrUndefined(normalized.rating) ??
    numberOrUndefined(objectValue(profile.ratingSummary)?.overallRating) ??
    numberOrUndefined(objectValue(profile.rating)?.value);
  const reviewCount =
    numberOrUndefined(record.reviewCount) ??
    numberOrUndefined(normalized.reviewCount) ??
    numberOrUndefined(objectValue(profile.ratingSummary)?.reviewCount) ??
    numberOrUndefined(objectValue(profile.rating)?.reviewCount);

  const achieved: string[] = [];
  const signal = (key: string, isAchieved: boolean) => {
    if (isAchieved) achieved.push(key);
  };

  // Trust signals (max 60)
  signal(
    "website_live",
    Boolean(firstSanitizedHttpUrl(socialLinks.website, record.websiteUrl, normalized.websiteUrl, contact.website)),
  );
  // Crawler records exist because they were found on the Shopify directory.
  signal(
    "shopify_directory_listing",
    Boolean(stringOrUndefined(record.sourceUrl) ?? stringOrUndefined(record.sourceSlug) ?? stringOrUndefined(profile.slug)),
  );
  signal("directory_reviews_25", (reviewCount ?? 0) >= 25);
  signal("rating_4_8", (rating ?? 0) >= 4.8);
  signal("years_in_business_5", yearsInBusiness(profile) >= 5);

  // Verification signals (max 40)
  // Crawler-sourced records are, by definition, not claimed on shopexperts yet.
  signal("profile_claimed_verified", false);
  signal("work_email_confirmed", Boolean(stringOrUndefined(contact.email)));
  signal("owner_linkedin_verified", Boolean(sanitizeLinkedInUrl(socialLinks.linkedin_url)));
  // Shopexperts-internal reviews only exist after a profile lives on shopexperts.
  signal("shopexperts_reviews_3", false);
  signal("case_study_published", (expert.customer_stories?.length ?? 0) >= 1);
  signal("service_published", (expert.packaged_services?.length ?? 0) >= 1);
  signal("listing_recently_updated", updatedWithinDays(record, 30));

  return computeCrawlerTrust(achieved);
}

/** Years since the scraped "partner since" value (e.g. "October 2020" or "2010"). */
function yearsInBusiness(profile: RawObject): number {
  const since = stringOrUndefined(profile.partnerSince);
  if (!since) return 0;
  const yearMatch = since.match(/(19|20)\d{2}/);
  if (!yearMatch) return 0;
  const year = Number(yearMatch[0]);
  const monthNames = [
    "january", "february", "march", "april", "may", "june",
    "july", "august", "september", "october", "november", "december",
  ];
  const normalizedSince = since.toLowerCase();
  const monthIndex = monthNames.findIndex((month) => {
    const abbreviation = month.slice(0, 3);
    return new RegExp(`\\b(?:${month}|${abbreviation})\\b`).test(normalizedSince);
  });
  const now = new Date();
  const beforeAnniversaryMonth = monthIndex >= 0 && now.getMonth() < monthIndex;
  return Math.max(0, now.getFullYear() - year - Number(beforeAnniversaryMonth));
}

function updatedWithinDays(record: PartnerDirectoryRecord, days: number): boolean {
  const raw = (record as unknown as Record<string, unknown>).updatedAt ?? (record as unknown as Record<string, unknown>).updated_at;
  const value = typeof raw === "string" || typeof raw === "number" ? new Date(raw) : raw instanceof Date ? raw : null;
  if (!value || Number.isNaN(value.getTime())) return false;
  return Date.now() - value.getTime() <= days * 24 * 60 * 60 * 1000;
}

function mapExtraPassthrough(mapped: RawObject | null): Record<string, unknown> | undefined {
  if (!mapped) return undefined;

  const extra: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(mapped)) {
    if (KNOWN_MAPPED_KEYS.has(key)) continue;
    if (value === undefined || value === null) continue;
    extra[key] = value;
  }

  return Object.keys(extra).length ? extra : undefined;
}

// ---------------------------------------------------------------------------
// Local value helpers (kept self-contained; mirror the scraper page helpers).
// ---------------------------------------------------------------------------

type RawObject = Record<string, unknown>;

function objectValue(value: unknown): RawObject | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as RawObject) : null;
}

function arrayValue(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function stringOrUndefined(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

/** Allow only absolute HTTP(S) destinations, normalizing bare hostnames to HTTPS. */
function sanitizeHttpUrl(value: unknown): string | undefined {
  const raw = stringOrUndefined(value);
  if (!raw) return undefined;

  try {
    const withScheme = raw.startsWith("//")
      ? `https:${raw}`
      : /^[a-z][a-z\d+.-]*:/i.test(raw)
        ? raw
        : `https://${raw}`;
    const parsed = new URL(withScheme);
    if (!["http:", "https:"].includes(parsed.protocol) || !parsed.hostname || parsed.username || parsed.password) return undefined;
    return parsed.toString();
  } catch {
    return undefined;
  }
}

function firstSanitizedHttpUrl(...values: unknown[]): string | undefined {
  for (const value of values) {
    const url = sanitizeHttpUrl(value);
    if (url) return url;
  }
  return undefined;
}

function sanitizeLinkedInUrl(value: unknown): string | undefined {
  const url = sanitizeHttpUrl(value);
  if (!url) return undefined;
  const hostname = new URL(url).hostname.toLowerCase();
  return hostname === "linkedin.com" || hostname.endsWith(".linkedin.com") ? url : undefined;
}

function numberOrUndefined(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function clampRating(value: number | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? Math.min(5, Math.max(0, value)) : 0;
}

function uniqueStrings(values: unknown[]): string[] {
  return Array.from(
    new Set(
      values.map(stringOrUndefined).filter((value): value is string => Boolean(value)),
    ),
  );
}

function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}
